import React from 'react';
import useCustomFields from '@/lib/hooks/swr/useCustomFields';
import MatchingTableHeaders from '../constants/MatchingTableHeaders';
import {
  MatchType,
  type APIHeader,
} from '../lib/matchingTableHeadersConstants';
import type APIHeaderMatch from '../types/APIHeaderMatch';
import type CSVHeaderColumn from '../types/CSVHeaderColumn';

/**
 * A user has to match up their CSV columns with "API headers". When
 * they do so, they are stating that CSV column Foo will be used with
 * API header Bar. This hook handles the logic for CSV headers that
 * been matched the API header.
 */
export default function useMatchedHeaders() {
  /**
   * Matched headers is an array of tuples that reflects the choice a
   * user has made that "CSV column Foo will be used (or matched) with API
   * header Bar."
   */
  const [matchedHeaders, setMatchedHeaders] = React.useState<APIHeaderMatch[]>(
    [],
  );

  /**
   * headers can also be matched to custom field IDs.
   */
  const { data: customFields } = useCustomFields();

  /**
   * This is called when a user matches their CSV column with an API
   * header.
   */
  const updateHeaders = React.useCallback(
    (
      apiHeader: APIHeader,
      matchedHeader: CSVHeaderColumn,
      matchType: keyof typeof MatchType,
    ) => {
      if (matchType === MatchType.MATCHED) {
        const foundHardcoded = MatchingTableHeaders.find(
          ({ fieldName }) => fieldName === apiHeader,
        );

        if (foundHardcoded) {
          setMatchedHeaders((prev) => [
            ...prev,
            [foundHardcoded.fieldName, matchedHeader.index],
          ]);
          return;
        }

        const foundCustomField = customFields?.find(
          ({ customFieldId }) => customFieldId === apiHeader,
        );

        if (foundCustomField) {
          setMatchedHeaders((prev) => [
            ...prev,
            [foundCustomField.customFieldId, matchedHeader.index],
          ]);
        }
      } else {
        const found = matchedHeaders.find(
          ([fieldName, index]) =>
            fieldName === apiHeader && index === matchedHeader.index,
        );

        if (found) {
          setMatchedHeaders((prev) =>
            prev.filter(([fieldName, index]) => {
              if (fieldName !== apiHeader) {
                return true;
              }

              return index !== matchedHeader.index;
            }),
          );
        }
      }
    },
    [matchedHeaders, customFields],
  );

  return {
    matchedHeaders,
    updateHeaders,
  };
}
